import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Film, Loader2, Download, X } from "lucide-react";
import { Sparkle } from "@/components/icons";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import VideoGenModal from "@/components/VideoGenModal";

export default function VideoPanel({ projectId, projectTitle, defaultPrompt }) {
  const { refreshUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [videos, setVideos] = useState([]);

  const loadVideos = useCallback(async () => {
    try { const { data } = await api.get(`/projects/${projectId}/videos`); setVideos(data); } catch {}
  }, [projectId]);

  useEffect(() => { loadVideos(); }, [loadVideos]);

  useEffect(() => {
    const pending = videos.filter((v) => v.status === "queued" || v.status === "generating");
    if (pending.length === 0) return;
    const t = setInterval(async () => {
      let changed = false;
      const updated = await Promise.all(videos.map(async (v) => {
        if (v.status === "queued" || v.status === "generating") {
          try { const { data } = await api.get(`/videos/${v.id}/status`); if (data.status !== v.status || data.video_url !== v.video_url) changed = true; return data; }
          catch { return v; }
        }
        return v;
      }));
      if (changed) { setVideos(updated); refreshUser(); }
    }, 10000);
    return () => clearInterval(t);
  }, [videos, refreshUser]);

  const download = async (v) => {
    try {
      const res = await fetch(v.video_url); const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `video_${v.id.slice(0, 6)}.mp4`; a.click();
      URL.revokeObjectURL(url);
    } catch { window.open(v.video_url, "_blank"); }
  };

  return (
    <div className="mt-10" data-testid="video-section">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Film size={18} className="text-gold" />
          <h2 className="font-heading font-black uppercase tracking-tight text-2xl">Animation</h2>
          <span className="label-mono">Seedance 2.5</span>
        </div>
        <button onClick={() => setOpen(true)} data-testid="open-video-modal" className="flex items-center gap-2 bg-gold text-ink-950 font-heading font-bold px-6 py-3 hover:bg-goldlight transition-colors">
          <Sparkle size={18} /> Generate Video
        </button>
      </div>

      {videos.length === 0 ? (
        <div className="border border-dashed border-zinc-800 py-16 flex flex-col items-center text-center" data-testid="empty-videos">
          <Film size={36} className="text-zinc-700 mb-3" />
          <p className="text-zinc-400 text-sm">No videos yet. Turn your storyboard into motion.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {videos.map((v) => (
            <motion.div key={v.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-ink-900 border border-zinc-800" data-testid={`video-${v.id}`}>
              <div className="relative aspect-video bg-ink-950 flex items-center justify-center overflow-hidden">
                {v.status === "completed" && v.video_url ? (
                  <video src={v.video_url} controls className="w-full h-full object-cover" data-testid={`video-player-${v.id}`} />
                ) : v.status === "failed" ? (
                  <div className="text-center px-4"><X className="text-destructive mx-auto mb-2" size={24} /><p className="text-xs text-zinc-500">Failed — credits refunded</p></div>
                ) : (
                  <div className="text-center px-4"><Loader2 className="animate-spin text-gold mx-auto mb-2" size={26} /><p className="label-mono">{v.status === "generating" ? "Rendering…" : "Queued…"}</p></div>
                )}
              </div>
              <div className="p-3">
                <p className="text-sm text-zinc-300 line-clamp-2 leading-snug">{v.prompt}</p>
                <div className="flex items-center justify-between mt-3">
                  <span className="label-mono">{v.duration}s · {v.resolution}</span>
                  {v.status === "completed" && v.video_url && (
                    <button onClick={() => download(v)} data-testid={`download-video-${v.id}`} className="flex items-center gap-1 text-xs text-gold hover:underline"><Download size={13} /> MP4</button>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <VideoGenModal open={open} onOpenChange={setOpen} projectId={projectId} projectTitle={projectTitle} defaultPrompt={defaultPrompt} onDone={loadVideos} />
    </div>
  );
}
